import {
  trackTMSPage,
  updateAndSyncDatalayer,
} from '../tag-commander/datalayer';
import type { Datalayer } from '../tag-commander/types';
import { getConvId, getParamFromDatalayer } from '../tag-commander/utils';
import type { TrackTMSConfirmationParams } from './types';
import { getVirtualPageName } from './utils';

/**
 * Tracks the TMS confirmation page with the provided transaction ID and revenue.
 * @param {TrackTMSConfirmationParams} params - The confirmation tracking params
 */
export const trackTMSConfirmation = async ({
  transactionId,
  revenue,
  eventName,
  screenName,
}: TrackTMSConfirmationParams) => {
  const product = getParamFromDatalayer('product');
  await trackTMSPage(
    {
      env_template: getEnvTemplate(product, eventName),
      page_name: screenName || getVirtualPageName(eventName) || 'confirmation',
      g_conv_id: getConvId(),
      product,
      transaction_id: getParamFromDatalayer('transaction_id') || transactionId,
      transaction_r_marketing:
        getParamFromDatalayer('transaction_r_marketing') ||
        String(revenue ?? ''),
    },
    { skipFetch: true }
  );
  setPostBookingData({ product });
};

/**
 * Retrieves the environment template based on the product_code or product.
 * For ad-hoc transaction types (eventName !== 'purchase'), the per-product
 * dictionary is bypassed in favor of the `confirmation-${eventName}` convention.
 * @param {string} product - The product name
 * @param {string} eventName - Ad-hoc event name.
 * @returns {string} The env_template parameter value for TMS tracking
 */
const getEnvTemplate = (product: string, eventName: string): string => {
  if (eventName !== 'purchase') {
    return `confirmation-${eventName}`;
  }
  const productCode = getParamFromDatalayer('product_code');
  const productCodeMapping: { [key: string]: string } = {
    FI: 'FLIGHT',
    TR: 'TRAIN',
    HO: 'HOTEL',
    SP: 'SUBSCRIPTION',
    'ST-PB': 'SEAT-PB',
    'BG-PB': 'BAG-PB',
    'IN-PB': 'INSURANCE-PB',
  };
  const dictionary: { [key: string]: string } = {
    DYNPACK: 'confirmation',
    PRIZE_FREEZE_FLIGHT: 'confirmation',
    FLIGHT: 'confirmation',
    TRAIN: 'confirmation',
    XFARING: 'confirmation',
    HOTEL: 'hotel-bf-confirmation',
    SUBSCRIPTION: 'prime-confirmation',
    UPP: 'confirmation',
    'SEAT-PB': 'seat-PB',
    'BAG-PB': 'bag-PB',
    'INSURANCE-PB': 'check-in-PB',
  };
  return (
    dictionary[productCodeMapping[productCode]] ||
    dictionary[product] ||
    'confirmation'
  );
};

/**
 * Sets the post booking data in the datalayer if the product is FLIGHT, TRAIN or XFARING.
 * @param {Partial<Datalayer>} datalayer - The datalayer object containing the product information.
 */
const setPostBookingData = (datalayer: Partial<Datalayer>): void => {
  const { product: post_booking_origin } = datalayer || {};
  if (!['FLIGHT', 'TRAIN', 'XFARING'].includes(post_booking_origin)) {
    return;
  }
  updateAndSyncDatalayer({ post_booking_origin });
};
